import React from 'react'
import { GridRenderCellParams } from '@mui/x-data-grid-pro'
import i18n from 'config/i18n'

import { SubmissionState, SUBMISSION_VIEW_ENUM, PartiposterProfile, SocialMediaAccount } from 'state/submissions'
import { TaskState, StepState, LINK_IN_BIO_TYPE_ENUM } from 'state/tasks'
import { parseBackendDate } from 'services/dates'
import { TagCell } from 'components/Shared/MuiTable/Cell/TagCell'
import { UrlCell, UrlCellProps } from 'components/Shared/MuiTable/Cell/UrlCell'
import { StringCell } from 'components/Shared/MuiTable/Cell/StringCell'
import { CommentCell } from 'components/Shared/MuiTable/Cell/CommentCell'
import { Country, PLATFORM_ENUM, TASK_TYPE_ENUM } from 'services/api/constants'
import { TagContentType } from 'particles/Tag'
import { GridColDefProps } from '../SimpleTable/constants'
import { ProfileCell } from 'components/Shared/MuiTable/Cell/ProfileCell'
import { SocialMediaInfoCell } from 'components/Shared/MuiTable/Cell/SocialMediaInfoCell'
import { FIELDS_TYPE } from 'components/utils/filterAndSort'

export interface RowsProps {
    id: string
    uuid: string
    url: string
    postingTime: {
        value: string
        color?: string
    }
    linkInBioUrl: string
    editsRequested: {
        submission?: SubmissionState
        contentType: TagContentType
    }
    partiposterTags: {
        profileData: PartiposterProfile
        submissionUuid: string
        campaignUuid: string
    }
    lastReviewedBy: {
        value?: string
    }
    lastReviewedAt: {
        value?: string
    }
}

export const getColumns = (
    viewType: SUBMISSION_VIEW_ENUM | null,
    showLinkInBio: boolean,
    selectedStep: StepState | null | undefined,
    canManageTag: boolean,
    task: TaskState,
): GridColDefProps[] => {
    const hasPlatform = (platform: TASK_TYPE_ENUM) => {
        return task.type.includes(platform)
    }

    if (viewType === 'edits_requested') {
        return [
            {
                field: 'profile',
                type: 'string',
                headerName: i18n.t('submission.table.profile'),
                width: 160,
                minWidth: 60,
                sortable: false,
                renderCell: (props: GridRenderCellParams) => <ProfileCell {...props} />,
                subFields: [
                    {
                        field: 'name',
                        headerName: i18n.t('submission.table.nameLabel'),
                    },
                    {
                        field: 'gender',
                        headerName: i18n.t('submission.table.genderLabel'),
                    },
                    {
                        field: 'age',
                        headerName: i18n.t('submission.table.ageLabel'),
                    },
                ],
            },
            {
                field: 'socialMediaInfo',
                type: 'string',
                headerName: i18n.t('submission.table.socialMediaInfo'),
                width: 180,
                minWidth: 60,
                sortable: false,
                renderCell: (props: GridRenderCellParams) => <SocialMediaInfoCell {...props} />,
                subFields: [
                    ...(hasPlatform(TASK_TYPE_ENUM.IG_POST) || hasPlatform(TASK_TYPE_ENUM.IG_REEL)
                        ? [
                              {
                                  field: FIELDS_TYPE.IG_USERNAME,
                                  headerName: i18n.t('submission.table.instagramUsernameLabel'),
                              },
                          ]
                        : []),
                    ...(hasPlatform(TASK_TYPE_ENUM.FB_POST)
                        ? [
                              {
                                  field: FIELDS_TYPE.FB_USERNAME,
                                  headerName: i18n.t('submission.table.facebookUsernameLabel'),
                              },
                          ]
                        : []),
                    ...(hasPlatform(TASK_TYPE_ENUM.TIKTOK_POST)
                        ? [
                              {
                                  field: FIELDS_TYPE.TIKTOK_USERNAME,
                                  headerName: i18n.t('submission.table.tikTokUsernameLabel'),
                              },
                          ]
                        : []),
                ],
            },
            {
                field: 'url',
                headerName: i18n.t('submission.table.urlLabel'),
                minWidth: 170,
                flex: 3,
                renderCell: (props: UrlCellProps) => <UrlCell {...props} />,
                sortable: false,
                filterable: false,
            },
            ...(selectedStep && selectedStep.data.video_code_field_disable === false
                ? [
                      {
                          field: 'video_code',
                          headerName: i18n.t('submission.table.videoCodeLabel'),
                          minWidth: 170,
                          flex: 3,
                          sortable: false,
                          filterable: false,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                      },
                  ]
                : []),
            {
                field: 'editsRequested',
                type: 'string',
                headerName: i18n.t('submission.table.reasonEditRequestLabel'),
                width: 200,
                renderCell: (props: GridRenderCellParams) => <CommentCell {...props} />,
                sortable: false,
                filterable: false,
            },
            {
                field: 'linkInBioUrl',
                type: 'string',
                headerName: i18n.t('submission.table.linkInBioLabel'),
                minWidth: 180,
                flex: 3,
                renderCell: (props: UrlCellProps) => <UrlCell {...props} />,
                hide: !showLinkInBio,
                sortable: false,
                filterable: false,
            },
            {
                field: 'lastReviewedBy',
                type: 'string',
                headerName: i18n.t('submission.table.lastReviewedLabel'),
                minWidth: 180,
                renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                sortable: false,
                filterable: false,
            },
            {
                field: 'lastReviewedAt',
                type: 'string',
                headerName: i18n.t('submission.table.lastReviewedDateLabel'),
                minWidth: 180,
                renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                sortable: false,
                filterable: false,
            },
            ...(canManageTag
                ? [
                      {
                          field: 'partiposterTags',
                          headerName: i18n.t('submission.table.partiposterTagsLabel', 'Partiposter Tags'),
                          width: 240,
                          minWidth: 200,
                          renderCell: (props: GridRenderCellParams) => <TagCell {...props} />,
                          sortable: false,
                          filterable: false,
                      },
                  ]
                : []),
        ]
    } else {
        return [
            {
                field: 'profile',
                type: 'string',
                headerName: i18n.t('submission.table.profile'),
                width: 160,
                minWidth: 60,
                sortable: false,
                renderCell: (props: GridRenderCellParams) => <ProfileCell {...props} />,
                subFields: [
                    {
                        field: 'name',
                        headerName: i18n.t('submission.table.nameLabel'),
                    },
                    {
                        field: 'gender',
                        headerName: i18n.t('submission.table.genderLabel'),
                    },
                    {
                        field: 'age',
                        headerName: i18n.t('submission.table.ageLabel'),
                    },
                ],
            },
            {
                field: 'socialMediaInfo',
                type: 'string',
                headerName: i18n.t('submission.table.socialMediaInfo'),
                width: 180,
                minWidth: 60,
                sortable: false,
                renderCell: (props: GridRenderCellParams) => <SocialMediaInfoCell {...props} />,
                subFields: [
                    ...(hasPlatform(TASK_TYPE_ENUM.IG_POST) || hasPlatform(TASK_TYPE_ENUM.IG_REEL)
                        ? [
                              {
                                  field: FIELDS_TYPE.IG_USERNAME,
                                  headerName: i18n.t('submission.table.instagramUsernameLabel'),
                              },
                          ]
                        : []),
                    ...(hasPlatform(TASK_TYPE_ENUM.FB_POST)
                        ? [
                              {
                                  field: FIELDS_TYPE.FB_USERNAME,
                                  headerName: i18n.t('submission.table.facebookUsernameLabel'),
                              },
                          ]
                        : []),
                    ...(hasPlatform(TASK_TYPE_ENUM.TIKTOK_POST)
                        ? [
                              {
                                  field: FIELDS_TYPE.TIKTOK_USERNAME,
                                  headerName: i18n.t('submission.table.tikTokUsernameLabel'),
                              },
                          ]
                        : []),
                ],
            },
            {
                field: 'postingTime',
                type: 'dateTime',
                headerName: i18n.t('submission.table.publishedTimeLabel'),
                width: 150,
                renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                sortable: false,
                filterable: false,
            },
            {
                field: 'url',
                headerName: i18n.t('submission.table.urlLabel'),
                width: 170,
                renderCell: (props: UrlCellProps) => <UrlCell {...props} />,
                sortable: false,
                filterable: false,
            },
            ...(selectedStep && selectedStep.data.video_code_field_disable === false
                ? [
                      {
                          field: 'video_code',
                          headerName: i18n.t('submission.table.videoCodeLabel'),
                          width: 170,
                          sortable: false,
                          filterable: false,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                      },
                  ]
                : []),
            {
                field: 'linkInBioUrl',
                type: 'string',
                headerName: i18n.t('submission.table.linkInBioLabel'),
                width: 200,
                renderCell: (props: UrlCellProps) => <UrlCell {...props} />,
                hide: !showLinkInBio,
                sortable: false,
                filterable: false,
            },
            ...(viewType !== SUBMISSION_VIEW_ENUM.PENDING
                ? [
                      {
                          field: 'lastReviewedBy',
                          type: 'string',
                          headerName: i18n.t('submission.table.lastReviewedLabel'),
                          minWidth: 180,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                          sortable: false,
                          filterable: false,
                      },
                      {
                          field: 'lastReviewedAt',
                          type: 'string',
                          headerName: i18n.t('submission.table.lastReviewedDateLabel'),
                          minWidth: 180,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                          sortable: false,
                          filterable: false,
                      },
                  ]
                : []),
            ...(canManageTag
                ? [
                      {
                          field: 'partiposterTags',
                          headerName: i18n.t('submission.table.partiposterTagsLabel', 'Partiposter Tags'),
                          width: 240,
                          minWidth: 160,
                          renderCell: (props: GridRenderCellParams) => <TagCell {...props} />,
                          sortable: false,
                          filterable: false,
                      },
                  ]
                : []),
        ]
    }
}

export const getRows = (
    viewType: SUBMISSION_VIEW_ENUM | null,
    campaignUuid: string,
    submissions: SubmissionState[],
    profileById: Record<string, PartiposterProfile>,
    task: TaskState,
    country: Country,
): RowsProps[] => {
    return submissions.map(submission => {
        const profile = profileById[submission.partiposterId]
        const { social_media_accounts: socialMediaAccounts } = profile
        const igProfile = socialMediaAccounts.filter((account: any) => account.platform === PLATFORM_ENUM.IG)[0]
        const fbProfile = socialMediaAccounts.filter(
            (account: SocialMediaAccount) => account.platform === PLATFORM_ENUM.FB,
        )[0]
        const tiktokProfile = socialMediaAccounts.filter(
            (account: SocialMediaAccount) => account.platform === PLATFORM_ENUM.TIKTOK,
        )[0]
        let linkInBioUrl
        if (task && task.data) {
            if (task.data.link_in_bio_type == LINK_IN_BIO_TYPE_ENUM.PREDEFINED_LINK) {
                linkInBioUrl = task.data['link_in_bio_url']
            } else if (task.data.link_in_bio_type == LINK_IN_BIO_TYPE_ENUM.DYNAMIC_LINK) {
                if (submission.submission_task != undefined) {
                    linkInBioUrl = submission.submission_task.data['link_in_bio_url']
                }
            }
        }
        return {
            id: submission.uuid,
            uuid: submission.uuid,
            profile: { profile },
            socialMediaInfo: {
                profiles: [igProfile, fbProfile, tiktokProfile],
            },
            url:
                viewType === SUBMISSION_VIEW_ENUM.PENDING || viewType === SUBMISSION_VIEW_ENUM.EDITS_REQUESTED
                    ? submission.data.url
                    : submission.reviewed_data
                      ? submission.reviewed_data.url
                      : '',
            lastReviewedBy: {
                value: submission.reviewed_by_system
                    ? i18n.t('statusLabel.autoApproved')
                    : submission.moved_out_of_pending_by?.name,
            },
            lastReviewedAt: {
                value: submission.moved_out_of_pending_at
                    ? parseBackendDate(submission.moved_out_of_pending_at, country, { showTime: true })
                    : undefined,
            },
            video_code:
                viewType === SUBMISSION_VIEW_ENUM.PENDING || viewType === SUBMISSION_VIEW_ENUM.EDITS_REQUESTED
                    ? { value: submission.data.video_code }
                    : submission.reviewed_data
                      ? { value: submission.reviewed_data.video_code }
                      : { value: '' },
            postingTime:
                viewType === SUBMISSION_VIEW_ENUM.PENDING || viewType === SUBMISSION_VIEW_ENUM.EDITS_REQUESTED
                    ? { value: '' }
                    : submission.reviewed_data
                      ? { value: parseBackendDate(submission.reviewed_data.posting_time, country, { showTime: true }) }
                      : { value: '' },
            linkInBioUrl: linkInBioUrl,
            editsRequested:
                viewType === SUBMISSION_VIEW_ENUM.EDITS_REQUESTED
                    ? { submission: submission, contentType: 'warning' }
                    : { submission: undefined, contentType: 'warning' },
            partiposterTags: {
                profileData: profile,
                submissionUuid: submission.submission_uuid,
                campaignUuid: campaignUuid,
            },
        }
    })
}
